import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Pencil, Check, X, CalendarIcon, Eye, Save, Trash2 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { DateRange } from 'react-day-picker';
import { useLanguage } from '@/contexts/LanguageContext';

type Booking = {
  id: string;
  enquiry_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  apartment: string;
  checkin: string;
  checkout: string;
  guests: number;
  total_nights: number | null;
  total_amount: number;
  discount_amount: number | null;
  deposit_amount: number | null;
  remaining_balance: number | null;
  payment_status: 'unpaid' | 'deposit_received' | 'fully_paid';
  notes: string | null;
  created_at: string;
};

export default function AdminBookings() {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Booking>>({});
  const [editDateRange, setEditDateRange] = useState<DateRange | undefined>();
  const [payFilter, setPayFilter] = useState<'all' | 'unpaid' | 'deposit_received' | 'fully_paid'>('all');
  const [timeTab, setTimeTab] = useState<'upcoming' | 'past'>('upcoming');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [notesEdit, setNotesEdit] = useState<Record<string, string>>({});

  const load = async () => {
    let q = supabase.from('bookings').select('*').order('checkin', { ascending: true });
    if (payFilter !== 'all') q = q.eq('payment_status', payFilter);
    const { data } = await q;
    setBookings((data as Booking[]) ?? []);
  };

  useEffect(() => { load(); }, [payFilter]);

  const startEdit = (b: Booking) => {
    setEditingId(b.id);
    setEditForm({
      name: b.name, email: b.email, phone: b.phone, apartment: b.apartment,
      checkin: b.checkin, checkout: b.checkout, guests: b.guests,
      total_amount: b.total_amount, deposit_amount: b.deposit_amount,
      remaining_balance: b.remaining_balance,
      payment_status: b.payment_status, notes: b.notes,
    });
    setEditDateRange({ from: parseISO(b.checkin), to: parseISO(b.checkout) });
  };

  const updateDeposit = (deposit: number) => {
    const total = editForm.total_amount ?? 0;
    const remaining = Math.max(0, total - deposit);
    const status = deposit >= total ? 'fully_paid' : deposit > 0 ? 'deposit_received' : 'unpaid';
    setEditForm(p => ({ ...p, deposit_amount: deposit, remaining_balance: remaining, payment_status: status as Booking['payment_status'] }));
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const { remaining_balance, total_nights, ...payload } = editForm as any;
    const { error } = await supabase.from('bookings').update(payload).eq('id', editingId);
    if (error) { toast.error(t('failedToSave') + ': ' + error.message); return; }
    toast.success(t('bookingUpdated'));
    setEditingId(null);
    load();
  };

  const deleteBooking = async (id: string, name: string) => {
    if (!confirm(t('deleteBookingConfirm').replace('{name}', name))) return;
    const { error } = await supabase.from('bookings').delete().eq('id', id);
    if (error) { toast.error(t('failedToDelete') + ': ' + error.message); return; }
    toast.success(t('bookingDeleted'));
    load();
  };

  const saveNotes = async (id: string) => {
    await supabase.from('bookings').update({ notes: notesEdit[id] ?? '' }).eq('id', id);
    toast.success(t('notesSaved'));
    load();
  };

  const payLabels: Record<string, string> = {
    all: t('all'), unpaid: t('unpaid'), deposit_received: t('depositReceived'), fully_paid: t('fullyPaid'),
  };

  const payBadge = (s: string) => {
    const styles: Record<string, string> = {
      unpaid: 'bg-red-100 text-red-800',
      deposit_received: 'bg-amber-100 text-amber-800',
      fully_paid: 'bg-green-100 text-green-800',
    };
    return <span className={`px-2 py-1 rounded-sm text-xs font-medium ${styles[s] || ''}`}>{payLabels[s] || s}</span>;
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const upcomingBookings = bookings.filter(b => b.checkout >= today);
  const pastBookings = bookings.filter(b => b.checkout < today);
  const displayedBookings = timeTab === 'upcoming' ? upcomingBookings : pastBookings;

  const timeLabels: Record<string, string> = { upcoming: t('upcoming'), past: t('past') };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl font-light">{t('bookings')}</h1>
      </div>

      <div className="flex gap-2 mb-4">
        {(['upcoming', 'past'] as const).map(tab => (
          <button key={tab} onClick={() => setTimeTab(tab)}
            className={`px-4 py-2 text-sm tracking-wider uppercase rounded-sm transition-colors ${timeTab === tab ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground border border-border hover:bg-muted'}`}>
            {timeLabels[tab]} ({tab === 'upcoming' ? upcomingBookings.length : pastBookings.length})
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-8">
        {(['all', 'unpaid', 'deposit_received', 'fully_paid'] as const).map(f => (
          <button key={f} onClick={() => setPayFilter(f)}
            className={`px-3 py-1.5 text-xs tracking-wider uppercase rounded-sm transition-colors ${payFilter === f ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground border border-border hover:bg-muted'}`}>
            {payLabels[f]}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {displayedBookings.map(b => (
          <div key={b.id} className="bg-card border border-border rounded-sm overflow-hidden">
            <div className="p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium">{b.name}</p>
                <p className="text-xs text-muted-foreground">{b.email}</p>
                {b.phone && <p className="text-xs text-muted-foreground">{b.phone}</p>}
              </div>
              <div className="text-sm">
                <span className="capitalize font-serif">{b.apartment}</span>
              </div>
              <div className="text-xs">
                <p>{b.checkin}</p>
                <p className="text-muted-foreground">→ {b.checkout}</p>
              </div>
              <div className="text-sm">{b.total_nights ?? differenceInDays(parseISO(b.checkout), parseISO(b.checkin))} {t('nights').toLowerCase()}</div>
              <div className="text-sm font-medium">€{b.total_amount}</div>
              <div className="text-sm">{b.deposit_amount ? `€${b.deposit_amount}` : '—'}</div>
              <div className="text-sm font-medium">{b.remaining_balance != null ? `€${b.remaining_balance}` : '—'}</div>
              <div>{payBadge(b.payment_status)}</div>
              <div className="flex gap-1">
                <button onClick={() => setViewingId(viewingId === b.id ? null : b.id)} className="p-1.5 rounded-sm bg-muted text-muted-foreground hover:bg-muted/80"><Eye size={14} /></button>
                <button onClick={() => startEdit(b)} className="p-1.5 rounded-sm bg-muted text-muted-foreground hover:bg-muted/80"><Pencil size={14} /></button>
                <button onClick={() => deleteBooking(b.id, b.name)} title={t('delete')} className="p-1.5 rounded-sm bg-red-50 text-red-700 hover:bg-red-100"><Trash2 size={14} /></button>
              </div>
            </div>

            {(viewingId === b.id || editingId === b.id) && (
              <div className="border-t border-border p-4 bg-muted/10">
                {editingId === b.id ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('name')}</label>
                      <input value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('emailLabel')}</label>
                      <input value={editForm.email || ''} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('phoneLabel')}</label>
                      <input value={editForm.phone || ''} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('apartmentLabel')}</label>
                      <select value={editForm.apartment || ''} onChange={e => setEditForm(p => ({ ...p, apartment: e.target.value }))}
                        className="bg-transparent border border-border rounded-sm px-2 py-1 text-sm w-full">
                        <option value="hera">Hera</option>
                        <option value="zeus">Zeus</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('dates')}</label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <button className={cn("flex items-center gap-1.5 border border-border rounded-sm px-2 py-1 text-xs w-full", !editForm.checkin && "text-muted-foreground")}>
                            <CalendarIcon size={12} />
                            {editDateRange?.from && editDateRange?.to
                              ? `${format(editDateRange.from, 'dd/MM/yy')} → ${format(editDateRange.to, 'dd/MM/yy')}`
                              : t('selectDatesAdmin')}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                          <Calendar
                            mode="range"
                            selected={editDateRange}
                            onSelect={(range: DateRange | undefined) => {
                              setEditDateRange(range);
                              setEditForm(p => ({
                                ...p,
                                checkin: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                                checkout: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                              }));
                            }}
                            numberOfMonths={2}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('guestsLabel')}</label>
                      <input type="number" value={editForm.guests ?? ''} onChange={e => setEditForm(p => ({ ...p, guests: Number(e.target.value) }))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('totalAmount')} (€)</label>
                      <input type="number" value={editForm.total_amount ?? ''} onChange={e => {
                        const total = Number(e.target.value);
                        const deposit = editForm.deposit_amount ?? 0;
                        setEditForm(p => ({ ...p, total_amount: total, remaining_balance: Math.max(0, total - deposit) }));
                      }}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('depositPaid')}</label>
                      <input type="number" value={editForm.deposit_amount ?? ''} onChange={e => updateDeposit(Number(e.target.value))}
                        className="w-full bg-transparent border-b border-border py-1 text-sm focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('remainingBalance')}</label>
                      <p className="font-serif text-lg font-medium text-orange-600">€{editForm.remaining_balance ?? 0}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('paymentStatus')}</label>
                      <select value={editForm.payment_status || ''} onChange={e => setEditForm(p => ({ ...p, payment_status: e.target.value as Booking['payment_status'] }))}
                        className="bg-transparent border border-border rounded-sm px-2 py-1 text-xs w-full">
                        <option value="unpaid">{t('unpaid')}</option>
                        <option value="deposit_received">{t('depositReceived')}</option>
                        <option value="fully_paid">{t('fullyPaid')}</option>
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('notes')}</label>
                      <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                        rows={3} className="w-full bg-transparent border border-border rounded-sm p-2 text-sm focus:outline-none focus:border-accent resize-none" />
                    </div>
                    <div className="md:col-span-3 flex gap-2">
                      <button onClick={saveEdit} className="flex items-center gap-1.5 bg-accent text-accent-foreground px-4 py-2 text-xs tracking-wider uppercase rounded-sm hover:bg-accent/90">
                        <Check size={14} /> {t('save')}
                      </button>
                      <button onClick={() => setEditingId(null)} className="flex items-center gap-1.5 px-4 py-2 text-xs tracking-wider uppercase text-muted-foreground border border-border rounded-sm hover:bg-muted">
                        <X size={14} /> {t('cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('guestsLabel')}</span>{b.guests}</div>
                      <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('discount')}</span>{b.discount_amount ? `€${b.discount_amount}` : '—'}</div>
                      <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('created')}</span>{format(parseISO(b.created_at), 'dd MMM yyyy')}</div>
                      <div><span className="text-xs text-muted-foreground uppercase tracking-wider block">{t('enquiryId')}</span><span className="text-xs font-mono">{b.enquiry_id?.slice(0, 8) || '—'}</span></div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">{t('notes')}</label>
                      <div className="flex gap-2">
                        <textarea
                          value={notesEdit[b.id] ?? b.notes ?? ''}
                          onChange={e => setNotesEdit(prev => ({ ...prev, [b.id]: e.target.value }))}
                          rows={2}
                          className="flex-1 bg-transparent border border-border rounded-sm p-2 text-sm focus:outline-none focus:border-accent resize-none"
                          placeholder={t('addNotesPlaceholder')}
                        />
                        <button onClick={() => saveNotes(b.id)} className="self-end p-2 rounded-sm bg-accent text-accent-foreground hover:bg-accent/90">
                          <Save size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {displayedBookings.length === 0 && (
          <div className="bg-card border border-border rounded-sm p-12 text-center text-muted-foreground">
            {timeTab === 'upcoming' ? t('noUpcomingBookings') : t('noPastBookings')}
          </div>
        )}
      </div>
    </div>
  );
}
